import type { CSSProperties } from 'react';

import type { AllocatedPanel } from '@/types/panels';

import { PANEL_GAP_PX, PANEL_PADDING_PX } from './allocate';

/**
 * The custom properties a slot is positioned by. Extends CSSProperties so the
 * result reaches a `style` prop as it is: React's type carries no general
 * `--*` index signature, but a declared custom property widens it fine.
 */
export interface SlotStyle extends CSSProperties {
    '--slot-left': string;
    '--slot-width'?: string;
    '--overlay-inset'?: string;
}

/** The pointer-owned drag a preview belongs to; null between drags. */
interface DragRef<T> {
    current: T | null;
}

const EDGE_EPSILON = 1e-6;

function px(value: number): string {
    const roundedValue = Math.round(value);
    const clean = Math.abs(value - roundedValue) <= EDGE_EPSILON ? roundedValue : value;
    return `${clean}px`;
}

/**
 * Pixel geometry from the stage's left edge, so a stage that changes width
 * under the row moves no fixed-width panel (see `@property --slot-left`).
 */
export function getPanelSlotStyle(
    slot: Pick<AllocatedPanel, 'startPx' | 'widthPx'>,
    paddingPx = PANEL_PADDING_PX,
): SlotStyle {
    return {
        '--slot-left': px(paddingPx + slot.startPx),
        '--slot-width': px(slot.widthPx),
    };
}

/** The inset of a floating overlay from the stage's edges. */
const OVERLAY_INSET_PX = 8;

/**
 * Where an overlay floats. The slot hands the inset to panels.css as `--overlay-inset`, which
 * anchors the panel on the right; this left edge is where it starts from when it docks into the
 * row.
 */
export function getOverlaySlotStyle(widthPx: number, stageWidth: number): SlotStyle {
    return {
        '--slot-left': px(stageWidth - OVERLAY_INSET_PX - widthPx),
        '--slot-width': px(widthPx),
        '--overlay-inset': px(OVERLAY_INSET_PX),
    };
}

/** The centre of the gap before the panel starting at `startPx`, for its splitter. */
export function getPanelBoundaryStyle(startPx: number, paddingPx = PANEL_PADDING_PX): SlotStyle {
    return {
        '--slot-left': px(paddingPx + startPx - PANEL_GAP_PX / 2),
    };
}

function writeStyle(
    element: HTMLElement, style: Record<string, string | number | undefined>,
): void {
    for (const [name, value] of Object.entries(style)) {
        if (value !== undefined) {
            element.style.setProperty(name, String(value));
        }
    }
}

export function writePanelSlotPreview(
    element: HTMLElement, slot: Pick<AllocatedPanel, 'startPx' | 'widthPx'>,
): void {
    const style = getPanelSlotStyle(slot);
    writeStyle(element, {
        '--slot-preview-left': style['--slot-left'],
        '--slot-preview-width': style['--slot-width'],
    });
}

export function writePanelBoundaryPreview(element: HTMLElement, startPx: number): void {
    writeStyle(element, {
        '--slot-preview-left': getPanelBoundaryStyle(startPx)['--slot-left'],
    });
}

/**
 * Make the released preview and React-owned slot agree before preview mode is
 * removed. React commits the same values immediately afterward.
 */
export function writePanelSlotHandoff(
    element: HTMLElement, slot: Pick<AllocatedPanel, 'startPx' | 'widthPx'>,
): void {
    const style = getPanelSlotStyle(slot);
    writeStyle(element, {
        ...style,
        '--slot-preview-left': style['--slot-left'],
        '--slot-preview-width': style['--slot-width'],
    });
}

export function writePanelBoundaryHandoff(element: HTMLElement, startPx: number): void {
    const style = getPanelBoundaryStyle(startPx);
    writeStyle(element, {
        ...style,
        '--slot-preview-left': style['--slot-left'],
    });
}

export function clearPanelPreviews(stage: HTMLElement): void {
    stage.querySelectorAll<HTMLElement>('.panel, .panel-resize-handle').forEach((element) => {
        element.style.removeProperty('--slot-preview-left');
        element.style.removeProperty('--slot-preview-width');
    });
    stage.removeAttribute('data-resizing');
}

/** Abandon pointer-owned panel geometry and report whether a drag ended. */
export function cancelPanelPreview(
    dragRef: DragRef<{ frame: number | null }>, stage: HTMLElement | null,
): boolean {
    const activeCount = dragRef.current;
    if (!activeCount) {
        return false;
    }
    if (activeCount.frame !== null) {
        cancelAnimationFrame(activeCount.frame);
    }
    dragRef.current = null;
    if (stage) {
        clearPanelPreviews(stage);
    }
    return true;
}

/** Establish released edges with transitions disabled before preview mode ends. */
export function finishPanelHandoff(stage: HTMLElement): void {
    // A layout read forces the committed custom-property writes to resolve while
    // data-resizing still suppresses edge transitions. Removing the attribute
    // afterward cannot coalesce into a transition from the pre-drag slot.
    stage.querySelector('.panel')?.getBoundingClientRect();
    clearPanelPreviews(stage);
}

/** One identity, so PanelHost's `style` prop does not change every render. */
export const CHAT_DRAWER_PANEL_STYLE: CSSProperties = Object.freeze({
    left: 'var(--pad)',
    right: 'var(--ai-chat-rule-width)',
    width: 'auto',
});

export function writeChatWidthPreview(element: HTMLElement, width: number): void {
    element.style.setProperty('--chat-preview-w', `${width}px`);
}

export function clearChatWidthPreview(element: HTMLElement | null): void {
    if (!element) {
        return;
    }
    element.style.removeProperty('--chat-preview-w');
    element.removeAttribute('data-resizing');
}
